import React from "react";
import { ChevronDown, ChevronUp, Layers, RefreshCw } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { fetchSimulationsByProduct } from "@/lib/api/manufacturingSimulation";
import { formatMoney } from "@/lib/api/manufacturing";

function formatDate(value) {
  if (!value) return "";
  return new Date(`${value}T00:00:00`).toLocaleDateString("es-ES");
}

function sumProductTotals(simulation, productId) {
  const items = (simulation.items || []).filter((item) => item.product_id === productId);
  return items.reduce(
    (acc, item) => ({
      quantity: acc.quantity + Number(item.total_quantity || 0),
      cost: acc.cost + Number(item.total_cost || 0),
    }),
    { quantity: 0, cost: 0 }
  );
}

export function ProductSimulationsGrid({ productId, reloadKey = 0 }) {
  const [simulations, setSimulations] = React.useState([]);
  const [status, setStatus] = React.useState("saved");
  const [loading, setLoading] = React.useState(false);
  const [expanded, setExpanded] = React.useState(false);

  const loadSimulations = React.useCallback(async () => {
    if (!productId) {
      setSimulations([]);
      return;
    }
    setLoading(true);
    const result = await fetchSimulationsByProduct(productId, { status, limit: 100 });
    setSimulations(result.data || []);
    setLoading(false);
  }, [productId, status]);

  React.useEffect(() => {
    if (expanded) loadSimulations();
  }, [expanded, loadSimulations, reloadKey]);

  return (
    <div className="rounded-lg border bg-white shadow-sm">
      <div className="flex flex-col gap-3 p-4 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-center gap-2">
          <Layers className="h-5 w-5 text-blue-600" />
          <div>
            <h3 className="font-semibold">Simulaciones de órdenes de fabricación</h3>
            <p className="text-sm text-gray-600">Simulaciones en las que se ha añadido este artículo.</p>
          </div>
          {expanded && !loading && (
            <Badge variant="secondary">{simulations.length}</Badge>
          )}
        </div>
        <div className="flex items-center gap-2">
          {expanded && (
            <>
              <select className="rounded-md border bg-white px-3 py-2 text-sm" value={status} onChange={(event) => setStatus(event.target.value)}>
                <option value="saved">Activas</option>
                <option value="archived">Archivadas</option>
                <option value="all">Todas</option>
              </select>
              <Button type="button" variant="outline" size="sm" onClick={loadSimulations} disabled={loading}>
                <RefreshCw className="mr-2 h-4 w-4" />Recargar
              </Button>
            </>
          )}
          <Button type="button" variant="outline" size="sm" onClick={() => setExpanded((current) => !current)}>
            {expanded ? <ChevronUp className="mr-2 h-4 w-4" /> : <ChevronDown className="mr-2 h-4 w-4" />}
            {expanded ? "Colapsar" : "Ver simulaciones"}
          </Button>
        </div>
      </div>

      {expanded && (
        <div className="border-t p-4">
          {loading ? (
            <p className="text-sm text-gray-500">Cargando simulaciones...</p>
          ) : simulations.length === 0 ? (
            <p className="text-sm text-gray-500">Este artículo no se ha añadido a ninguna simulación.</p>
          ) : (
            <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
              {simulations.map((simulation) => {
                const totals = sumProductTotals(simulation, productId);
                return (
                  <div key={simulation.id} className="flex flex-col gap-2 rounded-lg border bg-gray-50 p-3">
                    <div className="flex items-start justify-between gap-2">
                      <span className="text-xs text-gray-500">{formatDate(simulation.simulation_date)}</span>
                      <Badge variant={simulation.status === "archived" ? "outline" : "secondary"}>
                        {simulation.status === "archived" ? "Archivada" : "Activa"}
                      </Badge>
                    </div>
                    <p className="line-clamp-2 text-sm font-medium text-gray-800">{simulation.description}</p>
                    <div className="mt-auto grid grid-cols-2 gap-2 pt-1">
                      <div className="rounded border bg-white px-2 py-1 text-right">
                        <div className="text-[10px] uppercase text-gray-500">Prendas</div>
                        <div className="text-sm font-bold">{totals.quantity}</div>
                      </div>
                      <div className="rounded border bg-white px-2 py-1 text-right">
                        <div className="text-[10px] uppercase text-gray-500">Coste artículo</div>
                        <div className="text-sm font-bold text-blue-900">{formatMoney(totals.cost)}</div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
